import React, { useState, useRef, useEffect, memo } from 'react';
import { CursorNote } from './CursorNote';
import { GRID_MEASURE_LEN, NUM_MEASURE, timePosToY, LANE_WIDTH, keyLaneInfo, trackLaneInfo, xToKeyLane, xToTrackLane, NUM_BGM_LANES as NUM_TRACKS, SNAP_DISTANCE, NOTE_HEIGHT, ZOOM_STEP, yToTimePos, GRID_EDIT_DIV, keyLaneToX } from './lane';
import { LaneComponent } from './LaneComponent';
import { Fraction } from 'mathjs';
import { Notes } from './Notes';
import { HorizontalGrids } from './HorizontalGrids';
import * as math from 'mathjs';
import { CursorNotePosition, CursorTrackNotePosition, CursorKeyNotePosition, TrackNote, Tool } from '../store/types';
import { SequenceContainer, jsonToSequence } from '../store/SequenceContainer';

interface Point2D {
  x: number;
  y: number;
}

function isModalTool(tool: Tool) {
  switch (tool) {
    case Tool.Stairs:
    case Tool.MonoStairs:
    case Tool.Trill:
      return true;
    default:
      return false;
  }
}

// trackNotesのうちの時間位置が最も近いノートを取得して返す
function computeNearestNote(trackNotes: TrackNote[], y: number, verticalZoom: number)
  : [TrackNote, number] | null {

  if (trackNotes.length === 0) return null;

  let nearest = trackNotes[0];
  let nearestDist = Math.abs(timePosToY(nearest.timePos, verticalZoom) - NOTE_HEIGHT / 2 - (y + 1));

  for (let i = 1; i < trackNotes.length; i++) {
    const dist = Math.abs(timePosToY(trackNotes[i].timePos, verticalZoom) - NOTE_HEIGHT / 2 - (y + 1));
    if (dist < nearestDist) {
      nearest = trackNotes[i];
      nearestDist = dist;
    }
  }

  return [nearest, nearestDist];
}

const KEYCODE_CTRL = 17;

export const Sequence: React.FC = memo(() => {
  const {
    edit,
    keyNotes, setKeyNotes, trackNotesList, setTrackNotesList,
    nextKeyNoteId, setNextKeyNoteId, trackNextIds, setTrackNextIds,
    addKeyNotes, addKeyNotesRange, deleteKeyNotes, deleteKeyNotesRange,
    addTrackNotes, addTrackNotesRange, deleteTrackNotes, deleteTrackNotesRange,
    startApplyTool, applyStairs, applyMonoStairs, applyTrill,
  } = SequenceContainer.useContainer();

  const [panelSize, setPanelSize] = useState({ width: 0, height: 0 });
  const [scroll, setScroll] = useState({ x: 0, y: 0 } as Point2D);
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [mousePosition, setMousePosition] = useState<Point2D | null>(null);
  const [mouseButtons, setMouseButtons] = useState(0);
  const [applyingTool, setApplyingTool] = useState<{ timePos: Fraction, lane: number } | null>(null);

  const panel = useRef<HTMLDivElement>(null);

  // カーソルノートの位置を計算
  const cursorPos: CursorNotePosition | null = (() => {
    if (!mousePosition) return null;

    const x = mousePosition.x + scroll.x;
    const y = mousePosition.y + scroll.y;
    const notes = trackNotesList[edit.selectedTrack];

    if (ctrlPressed) {
      // トラックが対象
      const trackLane = xToTrackLane(x);
      if (trackLane === null) return null;

      // カーソルと時間位置が等しいかそれ以前のグリッドのうち、最も後ろのものを時間位置とする
      const timePos = math.chain(yToTimePos(y, edit.verticalZoom))
        .multiply(GRID_EDIT_DIV)
        .floor()
        .divide(GRID_EDIT_DIV).done() as Fraction;

      // カーソルのトラックのうち、時間位置が等しいトラックノートを取得
      let note = null;
      for (const n of trackNotesList[trackLane]) {
        if (math.equal(n.timePos, timePos)) {
          note = n;
          break;
        }
      }

      return {
        isKey: false,
        track: trackLane,
        timePos,
        note
      } as CursorTrackNotePosition;

    } else {
      // キーが対象
      const lane = xToKeyLane(x);
      if (lane === null) return null;

      // カーソルのトラックのうち、最も近いトラックノートを取得
      const ret = computeNearestNote(notes, y, edit.verticalZoom);
      if (!ret) return null;
      const [nearest, nearestDist] = ret;

      // 最短スナップ距離以下ならそのノートの時間位置にカーソル
      if (nearestDist > SNAP_DISTANCE) return null;

      // キーノートからレーンとトラックと時間位置が等しいノートを取得
      let note = null;
      for (const n of keyNotes) {
        if (n.lane === lane && n.track === edit.selectedTrack && math.equal(n.timePos, nearest.timePos)) {
          note = n;
          break;
        }
      }

      return {
        isKey: true,
        lane: lane,
        timePos: nearest.timePos,
        note
      } as CursorKeyNotePosition;
    }
  })();

  // 初期化時に最下部へスクロール
  useEffect(() => {
    if (panel.current) panel.current.scrollTo(0, panel.current.scrollHeight);
  }, []);

  //パネルサイズを取得
  useEffect(() => {
    function handleResize() {
      const cur = panel.current;
      if (cur) setPanelSize({ width: cur.clientWidth, height: cur.clientHeight });
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // キーボードイベントを登録
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.keyCode === KEYCODE_CTRL) {
        setCtrlPressed(true);
      }
    }
    function handleKeyUp(e: KeyboardEvent) {
      if (e.keyCode === KEYCODE_CTRL) {
        setCtrlPressed(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [keyNotes, trackNotesList]);

  // マウスイベントを登録
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      setMouseButtons(e.buttons);
    }
    function handleMouseUp(e: MouseEvent) {
      setMouseButtons(e.buttons);
    }

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [mouseButtons]);

  // クリップボードイベントを登録
  useEffect(() => {
    function handleCopy(e: ClipboardEvent) {
      // 譜面を出力
      if (e.clipboardData) {
        e.clipboardData.setData('text/plain',
          JSON.stringify({ keyNotes, nextKeyNoteId, trackNotesList, trackNextIds }));
      }
      e.preventDefault();
    }
    function handlePaste(e: ClipboardEvent) {
      if (e.clipboardData) {
        try {
          const json = JSON.parse(e.clipboardData.getData('text/plain'));

          const { keyNotes, nextKeyNoteId, trackNotesList, trackNextIds } = jsonToSequence(json);
          setKeyNotes(keyNotes);
          setNextKeyNoteId(nextKeyNoteId);
          setTrackNotesList(trackNotesList);
          setTrackNextIds(trackNextIds);

        } catch (e) {
          console.error(e);
        }
      }
      e.preventDefault();
    }

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [keyNotes, trackNotesList, nextKeyNoteId, setKeyNotes, setNextKeyNoteId, setTrackNextIds, setTrackNotesList, trackNextIds]);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {

    const newScroll = {
      x: e.currentTarget.scrollLeft,
      y: e.currentTarget.scrollTop
    };
    handleDrag(mouseButtons, mousePosition, newScroll);

    setScroll(newScroll);
  }

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;

    if (applyingTool) {
      setApplyingTool(null);
      return;
    }

    if (e.button === 0) { // 左クリック
      if (cursorPos) {
        if (cursorPos.isKey) {
          if (isModalTool(edit.selectedTool)) {
            setApplyingTool({ timePos: cursorPos.timePos, lane: cursorPos.lane });
            startApplyTool(edit.selectedTrack, cursorPos.timePos, cursorPos.lane);
          } else {
            addKeyNotes(edit.selectedTrack, [cursorPos.timePos], cursorPos.lane);
          }
        } else {
          addTrackNotes(cursorPos.track, [cursorPos.timePos]);
        }
      } else {
        const trackLane = xToTrackLane(x);
        if (trackLane !== null) edit.setSelectedTrack(trackLane);
      }
    } else if (e.button === 2) { // 右クリック
      if (cursorPos) {
        if (cursorPos.isKey) {
          if (cursorPos.note) deleteKeyNotes([cursorPos.note.id]);
        } else {
          if (cursorPos.note) deleteTrackNotes(cursorPos.track, [cursorPos.note.id]);
        }
      }
    }

  }

  function handleMouseUp(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (e.button === 0) {
      setApplyingTool(null);
    }
  }

  function handleDrag(buttons: number, newMousePosition: Point2D | null, newScroll: Point2D) {
    if (applyingTool) {
      if (!newMousePosition) return;
      const x = newMousePosition.x + newScroll.x;

      const ret = computeNearestNote(trackNotesList[edit.selectedTrack], newMousePosition.y + newScroll.y, edit.verticalZoom);
      if (!ret) return;
      const note = ret[0];

      const startX = keyLaneToX(applyingTool.lane);
      const direction = (x < startX - 1) ? -1 : (startX - 1 + LANE_WIDTH <= x) ? 1 : 0;

      switch (edit.selectedTool) {
        case Tool.Stairs:
          return applyStairs(edit.selectedTrack, applyingTool.timePos, note.timePos, applyingTool.lane, direction);
        case Tool.MonoStairs:
          return applyMonoStairs(edit.selectedTrack, applyingTool.timePos, note.timePos, applyingTool.lane, direction);
        case Tool.Trill:
          {
            const keyLane = xToKeyLane(newMousePosition.x + newScroll.x);
            if (keyLane === null) return;
            return applyTrill(edit.selectedTrack, applyingTool.timePos, note.timePos, [applyingTool.lane, keyLane]);
          }
        default:
          return setApplyingTool(null);
      }

    } else {
      if (mousePosition && newMousePosition) {
        const keyLane = xToKeyLane(newMousePosition.x + newScroll.x);
        const trackLane = xToTrackLane(newMousePosition.x + newScroll.x);

        if (keyLane !== null) {
          // 開始地点、終了地点それぞれについて、スナップできるトラックノートがあればその地点を使い、
          // なければマウスの地点を使う
          const ret1 = computeNearestNote(trackNotesList[edit.selectedTrack], mousePosition.y + scroll.y, edit.verticalZoom);
          const ret2 = computeNearestNote(trackNotesList[edit.selectedTrack], newMousePosition.y + newScroll.y, edit.verticalZoom);

          if (ret1 && ret2) {
            const [note1, dist1] = ret1;
            const [note2, dist2] = ret2;

            const p1 = (dist1 <= SNAP_DISTANCE) ? note1.timePos : yToTimePos(mousePosition.y + scroll.y, edit.verticalZoom);
            const p2 = (dist2 <= SNAP_DISTANCE) ? note2.timePos : yToTimePos(newMousePosition.y + newScroll.y, edit.verticalZoom);

            const [from, to] = [p1, p2].sort((a, b) => math.compare(a, b) as number);

            if (edit.selectedTool === Tool.Paint && (buttons & 1) !== 0) {
              addKeyNotesRange(edit.selectedTrack, from, to, keyLane);
            } else if ((buttons & 2) !== 0) {
              deleteKeyNotesRange(edit.selectedTrack, from, to, keyLane);
            }
          }
        } else if (trackLane !== null) {
          if (ctrlPressed) {
            const p1 = math.chain(yToTimePos(mousePosition.y + scroll.y, edit.verticalZoom))
              .multiply(GRID_EDIT_DIV).floor().divide(GRID_EDIT_DIV).done() as Fraction;
            const p2 = math.chain(yToTimePos(newMousePosition.y + newScroll.y, edit.verticalZoom))
              .multiply(GRID_EDIT_DIV).floor().divide(GRID_EDIT_DIV).done() as Fraction;

            const [from, to] = [p1, p2].sort((a, b) => math.compare(a, b) as number);

            if (edit.selectedTool === Tool.Paint && (buttons & 1) !== 0) {
              addTrackNotesRange(trackLane, from, to);
            } else if ((buttons & 2) !== 0) {
              deleteTrackNotesRange(trackLane, from, to);
            }
          } else {
            if ((buttons & 1) !== 0) {
              edit.setSelectedTrack(trackLane);
            }
          }
        }
      }
    }
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const newMousePosition = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    handleDrag(e.buttons, newMousePosition, scroll);

    setMousePosition(newMousePosition);
  }

  function handleMouseOut(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();

    setMousePosition(null);
  }

  function handleWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (e.shiftKey && !applyingTool) {
      e.preventDefault();

      if (e.deltaY < 0) edit.setSelectedTrack((edit.selectedTrack + NUM_TRACKS - 1) % NUM_TRACKS);
      else if (e.deltaY > 0) edit.setSelectedTrack((edit.selectedTrack + 1) % NUM_TRACKS);

    } else if (e.ctrlKey) {
      e.preventDefault();

      if (e.deltaY < 0) edit.setVerticalZoom(edit.verticalZoom + ZOOM_STEP);
      else if (e.deltaY > 0 && edit.verticalZoom > ZOOM_STEP) edit.setVerticalZoom(edit.verticalZoom - ZOOM_STEP);
    }
  }

  return (
    <div className="Sequence" ref={panel}
      onScroll={handleScroll}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseOut={handleMouseOut}>

      <div className="area"
        style={{
          width: trackLaneInfo[trackLaneInfo.length - 1].left + LANE_WIDTH,
          height: GRID_MEASURE_LEN * NUM_MEASURE * edit.verticalZoom
        }}>

        <div>
          {keyLaneInfo.map((lane, i) =>
            <LaneComponent key={i}
              {...lane}
              selected={false} />
          )}
          {trackLaneInfo.map((lane, i) =>
            <LaneComponent key={i}
              {...lane}
              selected={edit.selectedTrack === i} />
          )}
        </div>

        <HorizontalGrids panelScrollY={scroll.y}
          panelHeight={panelSize.height} />

        <Notes notes={keyNotes}
          selectedTrack={edit.selectedTrack}
          panelScrollY={scroll.y}
          panelHeight={panelSize.height} />

        {trackNotesList.map((notes, i) =>
          <Notes key={i} notes={notes}
            selectedTrack={edit.selectedTrack}
            panelScrollY={scroll.y}
            panelHeight={panelSize.height} />
        )}

        {cursorPos &&
          <CursorNote position={cursorPos} />
        }

      </div>

    </div>
  );
});
